import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CardStat from 'components/CardStat';
import DiagramSemiCircle from 'components/DiagramSemiCircle';
import Talk from 'images/icons/Talk.svg?react';
import { useDateRangeStore } from 'stores/dateRange';
import { useContactsQuery } from 'core/api/contacts';
import { useTasksSortedByDeadline } from 'core/api/tasks';
import { isDateInDateRange, substractNbOfDay } from 'utils/dateTimeFormat';
import { computeProgression } from 'core/useCases/dashboard/utils';
import { getEngagementDateOfContacts } from 'core/useCases/contacts/utils';
import { useDashboardTresholds } from 'core/api/dashboardTresholds';

const FirstTaskDiagram: FC = () => {
    const { t } = useTranslation();
    const dateRangeSelected = useDateRangeStore((s) => s.dateRange);
    const { data: contactsData } = useContactsQuery();
    const contacts = useMemo(() => contactsData ?? [], [contactsData]);
    const { data: tasksData } = useTasksSortedByDeadline();
    const tasks = useMemo(() => tasksData ?? [], [tasksData]);
    const { firstTaskLow = 0, firstTaskMedium = 0, firstTaskHigh = 0 } = useDashboardTresholds() ?? {};
    const [firstTasksByContactId, setFirstTasksByContactId] = useState<Array<{ contactId: String; date: Date }>>([]);
    const [value, setValue] = useState<number>();
    const [progression, setProgression] = useState<string>('');

    useEffect(() => {
        setFirstTasksByContactId(getEngagementDateOfContacts(contacts, tasks));
    }, [contacts, tasks]);

    useEffect(() => {
        const total = firstTasksByContactId.filter((item) =>
            isDateInDateRange(new Date(item.date), dateRangeSelected),
        ).length;

        const { startDate, endDate } = dateRangeSelected;
        const previousDateRange = {
            endDate: substractNbOfDay(endDate, 7),
            startDate: substractNbOfDay(startDate, 7),
        };
        const previousTotal = firstTasksByContactId.filter((item) =>
            isDateInDateRange(new Date(item.date), previousDateRange),
        ).length;

        setValue(total);
        setProgression(previousTotal && total ? computeProgression(previousTotal, total) : '');
    }, [firstTasksByContactId, dateRangeSelected]);

    const firstTask = [
        {
            color: '#FFDDC3',
            key: 'low',
            rangeMax: firstTaskLow,
            rangeMin: 0,
        },
        {
            color: '#FFBF8B',
            key: 'middle',
            rangeMax: firstTaskMedium,
            rangeMin: firstTaskLow,
        },
        {
            color: '#FFAC69',
            key: 'high',
            rangeMax: firstTaskHigh,
            rangeMin: firstTaskMedium,
        },
    ];

    return value !== undefined ? (
        <CardStat icon={<Talk />} progression={progression} title={t('diagram.first-task.title')} value={value}>
            <DiagramSemiCircle data={firstTask} value={value} />
        </CardStat>
    ) : null;
};

export default FirstTaskDiagram;
